import type { ComponentChildren } from "preact";
import { Suspense } from "preact/compat";
import { lazyWithReload } from "./lazy-reload";
import { useEffect, useLayoutEffect, useRef, useState } from "preact/hooks";
import { api, onNameHeld, onUnauthorized } from "./api";
import { back, navigate, parseHash, previousRouteName, useRoute, type Route } from "./router";
import { getPeonName, resetHouseholdState, setPeonName } from "./store";
import type { AuthState, Role } from "../shared/types";
import { InstallHint, shouldShowInstallHint } from "./components/InstallHint";
import { closeTour, playWholeTour, tourCardsShown, useTour } from "./tour-moments";
import { closeWhatsNew, startWhatsNew, useWhatsNew, whatsNewShows } from "./whats-new";
import { closeWeekly, startWeekly, useWeeklySheet } from "./weekly-sheet";
import { closeCheerJobs, closeCheered, nextCheerJob, startCheers, useCheerSheets } from "./cheer-sheet";
import { closeGoal, useGoalSheet } from "./goal-sheet";
import { weeklySheetShows, whatsNewWaitsForWeekly } from "../shared/weekly.ts";
import { WeeklySheet } from "./components/WeeklyScroll";
import { closeSpin, rouletteShows, startRoulette, useRoulette, useWheelBeforeTask } from "./roulette";
import { NotifyPrompt, shouldShowNotifyPrompt } from "./components/NotifyPrompt";
import { DEV_EVENT } from "./devsheet-hooks";
import { WORKSHOP_REFUSAL_LINE, canOpenWorkshop, joinCodeFromPath } from "./members-ui.ts";
import { heldFrom } from "./passwords-ui.ts";
import { rememberHeldNames, rememberPersonSignIn } from "./gate-memory.ts";
import { setFlagsHousehold } from "./flags-core.ts";
import { setActiveHousehold } from "./format";
import { sayText } from "./speech";
import { FEEDBACK_TITLE } from "../shared/feedback.ts";
import { prefersReducedMotion } from "./components/useExit";
import { currentFlags, useCheersUI, useFeedbackUI, useHouseholdFlagsLoaded, useHouseholdFlagsSync, useRecapUI, useRouletteUI, useTrinketsUI, useUpgradesUI, useWeeklyUI } from "./features";
import { closeFind, usePendingFind } from "./trinket-found";
import { installNativePushListeners, installPushSoundListener, syncPushName } from "./push";
import { isNativeShell } from "./components/InstallHint";
import { startPerf } from "./perf.ts";
import { startTelemetry } from "./telemetry";
import { startMemoryReports } from "./memory";
import { installBackSwipe, isTabRoute, parentRoute } from "./back-swipe";
import { focusContentPane, installDesktopKeys } from "./desktop-keys.ts";
import { arrivalSlide, slideMoves, type Entry, type Slide } from "./screen-slide.ts";
import { recallTask } from "./board-cache";
import { recallUpgradeTitle } from "./upgrade-titles";
import { Header, Spinner, WhoButton } from "./components/Shell";
import { boardRequestKey, forgetPrefetchedBoards, prefetchBoard, startOfLastMonth, startOfThisMonth, type BoardRequestOpts } from "./prefetch";
import { hall } from "./hall-cache";
import { Board } from "./views/Board";
import { HallOfFame } from "./views/HallOfFame";
import { Login, Unreachable } from "./views/Login";
import { NameGate } from "./views/NameGate";
import { Rewards } from "./views/Rewards";
import { ScheduleForm } from "./views/ScheduleForm";
import { Schedules } from "./views/Schedules";
import { TaskDetail } from "./views/TaskDetail";
import { TaskForm } from "./views/TaskForm";
import { PeonWorkloadSync } from "./components/PeonWorkloadSync";
import { RecapIntro, RecapPlayer, useRecapLayers } from "./components/Recap";
import { startPeonClickAudio, startPeonIdleAudio, startScreenAudio, startUiClickAudio } from "./audio";

// Lazy: the workshop (DevSheet, ~900 lines) and the tutorial ship a lot of
// copy that almost nobody opens on a given launch. Loaded on demand so the
// entry chunk stays small (T10, docs/performance.md C3); once fetched, the
// chunk is cached by the service worker like any other /assets/ file.
const DevSheet = lazyWithReload(() => import("./components/DevSheet").then((m) => ({ default: m.DevSheet })));
const Tutorial = lazyWithReload(() => import("./components/Tutorial").then((m) => ({ default: m.Tutorial })));
// AccountSheet only opens from a tap on the peon's name, so it does not need to be in the entry chunk either.
const Usage = lazyWithReload(() => import("./views/Usage").then((m) => ({ default: m.Usage })));
// Dynasty upgrades (docs/upgrades.md): off for most households, so its three screens load on demand.
const Upgrades = lazyWithReload(() => import("./views/Upgrades").then((m) => ({ default: m.Upgrades })));
const UpgradeForm = lazyWithReload(() => import("./views/UpgradeForm").then((m) => ({ default: m.UpgradeForm })));
const UpgradeDetail = lazyWithReload(() => import("./views/UpgradeDetail").then((m) => ({ default: m.UpgradeDetail })));
const AccountSheet = lazyWithReload(() => import("./components/AccountSheet").then((m) => ({ default: m.AccountSheet })));
// Suggest something's form, for What's new's link (#/feedback); About opens it inside AccountSheet's chunk.
const FeedbackSheet = lazyWithReload(() => import("./components/FeedbackSheet").then((m) => ({ default: m.FeedbackSheet })));
// Run the house (docs/members.md): stewards only, a pushed screen opened from the account sheet.
const House = lazyWithReload(() => import("./views/House").then((m) => ({ default: m.House })));
// The character editor (#/peon): a pushed screen opened from your portrait in the account sheet.
const PeonEditor = lazyWithReload(() => import("./views/PeonEditor").then((m) => ({ default: m.PeonEditor })));
/** Trinkets (docs/trinkets.md), behind trinketsUI: the collection screen and the find sheet, in their own chunk. */
const Trinkets = lazyWithReload(() => import("./views/Trinkets").then((m) => ({ default: m.Trinkets })));
const TrinketFound = lazyWithReload(() => import("./components/TrinketFound").then((m) => ({ default: m.TrinketFound })));
// What's new (src/whats-new.ts): shows once after an update, so it loads only then.
const WhatsNew = lazyWithReload(() => import("./components/WhatsNew").then((m) => ({ default: m.WhatsNew })));
// Cheers (src/cheer-sheet.ts): the sheets load only when a job or a cheer is waiting.
const CheerSheet = lazyWithReload(() => import("./components/CheerSheet").then((m) => ({ default: m.CheerSheet })));
const CheeredSheet = lazyWithReload(() => import("./components/CheerSheet").then((m) => ({ default: m.CheeredSheet })));
// The house goal's celebration (src/goal-sheet.ts): loads only when a reached week is waiting.
const GoalSheet = lazyWithReload(() => import("./components/GoalSheet").then((m) => ({ default: m.GoalSheet })));
const RouletteWheel = lazyWithReload(() => import("./components/RouletteWheel").then((m) => ({ default: m.RouletteWheel })));

/** Drops a `/join/<code>` path from the address bar, so a reload does not join again. */
function clearJoinPath(): void {
  if (location.pathname === "/") return;
  history.replaceState(null, "", `/${location.search}${location.hash}`);
}

/** Invisible: loads the household flags once mounted and refetches on every live change. Mounted only once authenticated. */
function HouseholdFlagsSync() {
  useHouseholdFlagsSync();
  return null;
}

/**
 * True while the browser says it has no connection: a muted "Offline" line at the top of the
 * content, so a board the app kept from before is not taken for a live one.
 */
function useOffline(): boolean {
  const [offline, setOffline] = useState(() => typeof navigator !== "undefined" && navigator.onLine === false);
  useEffect(() => {
    const update = () => setOffline(navigator.onLine === false);
    window.addEventListener("online", update);
    window.addEventListener("offline", update);
    return () => {
      window.removeEventListener("online", update);
      window.removeEventListener("offline", update);
    };
  }, []);
  return offline;
}

/** The header's accessible name for a route (the h1 is sr-only; a screen's visible title is its ScreenTitle). */
function screenTitle(route: Route): string {
  switch (route.name) {
    case "open":
      return "Work, work";
    case "done":
      return "Job's done";
    case "schedules":
      return "Schedules";
    case "task":
      return recallTask(route.id)?.title ?? "Task";
    case "newTask":
      return "New task";
    case "newSchedule":
      return "New schedule";
    case "schedule":
      return "Edit schedule";
    case "hall":
      return "Peon Hall of Fame";
    case "rewards":
      return "Rewards";
    case "usage":
      return "Household usage";
    case "house":
      return "Run the house";
    case "peon":
      return "Your peon";
    case "upgrades":
      return "Dynasty upgrades";
    case "newUpgrade":
      return "New upgrade";
    case "upgrade":
      return recallUpgradeTitle(route.id) ?? "Upgrade";
    case "trinkets":
      return "Trinkets";
    case "feedback":
      return FEEDBACK_TITLE;
  }
}

/**
 * When a screen mounts (the first one after a load or a sign-in included) and after every
 * navigation, the focus moves to the screen's content pane, so a keyboard or screen reader lands on
 * it instead of on the page body, and Space, the page keys, the arrows and Home scroll it at once
 * (owner, 2026-10-05: "A lot of interactions only make sense on mobile"; src/desktop-keys.ts). The
 * pane gets tabIndex -1 and no outline (styles.css). Left alone when the focus already sits inside
 * the new content (a form's autofocus field), in a field, or inside an open sheet, viewer or the tour.
 */
function useFocusOnNavigate(screenKey: string): void {
  useEffect(() => {
    const frame = requestAnimationFrame(() => focusContentPane());
    return () => cancelAnimationFrame(frame);
  }, [screenKey]);
}

/** The history entry's stamp (src/audio.ts gives each entry a rising opSeq), or null before it has one. */
function entryStamp(): number | null {
  const state = typeof history === "undefined" ? null : (history.state as Record<string, unknown> | null);
  const seq = state && typeof state === "object" ? state.opSeq : undefined;
  return typeof seq === "number" ? seq : null;
}

/**
 * The screen's arrival, from the move that reached it (src/screen-slide.ts arrivalSlide): "push" for
 * a move deeper, "back" for a return (a pop through history, the back link, any screen landing on a
 * tab), null for a tab switch or the first screen after a load. Decided once per route, during its
 * first render, so a re-render never restarts it.
 *
 * Only a push moves (src/screen-slide.ts slideMoves): the screen's scroller (`.screen > .content`;
 * the top bar, tab bar, add button and back-to-top arrow stay where they are) slides in from the
 * right. A return lands still, as the screen that was there all along (owner, 2026-10-04: the
 * revealed screen sliding in after a back swipe "looks weird"); its data-slide still says it
 * arrived by a move, so nothing inside it rises either. A script animation rather than a CSS one,
 * so a scroller that mounts again under the same route (a lazy screen replacing its spinner) never
 * slides a second time. Its numbers are the motion block's tokens in styles.css.
 */
function useSlide(routeKey: string, name: Route["name"]): Slide {
  const left = useRef<Entry | null>(null);
  const decided = useRef<{ key: string; slide: Slide } | null>(null);
  if (decided.current?.key !== routeKey) {
    const hash = typeof location === "undefined" ? "" : location.hash;
    decided.current = { key: routeKey, slide: arrivalSlide(left.current, { hash, seq: entryStamp() }, name) };
  }
  // Read after the render, when the screen audio's hashchange listener has stamped a new entry.
  useEffect(() => {
    left.current = { seq: entryStamp(), hash: typeof location === "undefined" ? "" : location.hash };
  }, [routeKey]);
  const slide = decided.current.slide;
  // Before the first paint, so the screen's first frame is already the slide's first frame.
  useLayoutEffect(() => {
    if (slideMoves(slide)) playSlide(slide);
  }, [routeKey]);
  return slide;
}

/** Fired on the document when a screen's slide lands (src/components/Tutorial.tsx listens). */
export const SLID_EVENT = "peons:slid";

/** Slides a pushed screen's scroller in from the right in hard frames. */
function playSlide(slide: "push"): void {
  if (prefersReducedMotion()) return;
  const pane = document.querySelector<HTMLElement>(".screen > .content");
  if (!pane || typeof pane.animate !== "function") return;
  const vars = getComputedStyle(document.documentElement);
  const token = (name: string) => vars.getPropertyValue(`--${slide}-${name}`).trim();
  // The build's minifier may write 160ms as .16s.
  const raw = token("time");
  const time = parseFloat(raw) * (raw.endsWith("ms") ? 1 : raw.endsWith("s") ? 1000 : 1);
  const steps = parseInt(token("steps"), 10);
  const from = token("from");
  if (!(time > 0) || !(steps > 0) || !from) return;
  const run = pane.animate([{ translate: `${from} 0` }, { translate: "0 0" }], { duration: time, easing: `steps(${steps}, end)`, fill: "backwards" });
  // A script animation fires no animationend: say when it lands (the tour re-measures its spotlight).
  run.addEventListener("finish", () => document.dispatchEvent(new Event(SLID_EVENT)));
}

export function App() {
  // The address's route; the screen drawn is `route` below, which differs only while a task waits
  // for its wheel (useWheelBeforeTask).
  const address = useRoute();
  const upgradesUIEnabled = useUpgradesUI();
  const trinketsUIEnabled = useTrinketsUI();
  // A find waiting to be shown (src/trinket-found.ts): the sheet over whatever is up, while the flag is on.
  const pendingFind = usePendingFind();
  const upgradeRoute = address.name === "upgrades" || address.name === "newUpgrade" || address.name === "upgrade";
  // A stale upgrade link with the flag off lands on the open board instead of an empty screen.
  // Waits for the household flags, so a device whose flag comes from the household is not bounced.
  const flagsLoaded = useHouseholdFlagsLoaded();
  useEffect(() => {
    if (upgradeRoute && !upgradesUIEnabled && flagsLoaded) navigate({ name: "open" }, true);
  }, [upgradeRoute, upgradesUIEnabled, flagsLoaded]);
  // Suggest something's link (#/feedback) does nothing special with its flag off: the board, as a stale upgrade link.
  const feedbackUIEnabled = useFeedbackUI();
  const feedbackAddress = address.name === "feedback";
  useEffect(() => {
    if (feedbackAddress && !feedbackUIEnabled && flagsLoaded) navigate({ name: "open" }, true);
  }, [feedbackAddress, feedbackUIEnabled, flagsLoaded]);
  const [name, setName] = useState<string | null>(() => getPeonName());
  /**
   * The held name a 403 name_held named (docs/members.md "Personal passwords"): this device used a
   * name someone holds without signing in as them. The stored name is forgotten and the name gate
   * opens with this name and its password field.
   */
  const [heldAs, setHeldAs] = useState<string | null>(null);
  const [auth, setAuth] = useState<AuthState | null>(null);
  /** The latest auth, for the logo-tap listener registered once at mount. */
  const authRef = useRef<AuthState | null>(null);
  authRef.current = auth;
  /**
   * Why a working session dropped to the gate (a steward signed this device out, or the household
   * went): one quiet line on the gate, until the next sign-in.
   */
  const [signedOutNotice, setSignedOutNotice] = useState<string | null>(null);
  const offline = useOffline();
  // What the rest of the client keys on the active household: the cached flags (src/flags-core.ts)
  // and the house name a chore is from (src/format.ts displayCreator).
  useEffect(() => {
    if (!auth?.authenticated || !auth.household) return;
    setFlagsHousehold(auth.household.id);
    setActiveHousehold(auth.household);
  }, [auth?.authenticated, auth?.household?.id, auth?.household?.name]);
  // Run the house is a steward's screen: anyone else signed in who lands on #/house goes to the board
  // (as a stale upgrade link does). Signed out, the gate shows first and this runs once it is passed.
  const notSteward = address.name === "house" && !!auth?.authenticated && auth.household?.role !== "steward";
  useEffect(() => {
    if (notSteward) navigate({ name: "open" }, true);
  }, [notSteward]);
  // A device signed in as a person (their own password or a passkey, or one just set) meets "Sign
  // in" first on the gate from now on (src/gate-memory.ts).
  useEffect(() => {
    if (auth?.person) rememberPersonSignIn();
  }, [auth?.person?.id]);
  /** The invite code from a `/join/<code>` launch, until it has been tried. */
  const [joinCode, setJoinCode] = useState<string | null>(() => joinCodeFromPath(location.pathname));
  const joinCodeUsed = () => {
    clearJoinPath();
    setJoinCode(null);
  };
  useEffect(() => {
    if (!auth?.authenticated || !name) return;
    const stopClicks = startPeonClickAudio();
    const stopIdle = startPeonIdleAudio();
    const stopTicks = startUiClickAudio();
    const stopScreens = startScreenAudio();
    return () => {
      stopClicks();
      stopIdle();
      stopTicks();
      stopScreens();
    };
  }, [auth?.authenticated, name]);
  const [showInstall, setShowInstall] = useState(() => shouldShowInstallHint());
  // The tour plays in moments where they happen (src/tour-moments.ts); the shell draws the one showing.
  const tour = useTour();
  /** What's new this device has not been shown, its flags on, once the board has loaded (src/whats-new.ts). */
  const whatsNew = useWhatsNew();
  /** The weekly scroll's sheet (src/weekly-sheet.ts): once a week, before What's new. */
  const weekly = useWeeklySheet();
  const weeklyUI = useWeeklyUI();
  /** Cheers (src/cheer-sheet.ts, docs/cheers.md): your cheered jobs, then the jobs to cheer, after the wheel. */
  const cheers = useCheerSheets();
  const cheersUI = useCheersUI();
  /** The house goal's celebration (src/goal-sheet.ts, docs/house-week.md): once per device per reached week. */
  const goalWeek = useGoalSheet();
  /** Set once What's new has shown this open: the weekly scroll then waits for it, never cutting it short. */
  const whatsNewUp = useRef(false);
  /** Set while the weekly scroll's sheet is up: a wheel asked for with its task waits for it, never cutting it short. */
  const weeklyUp = useRef(false);
  /** Task roulette's spins this device has not shown, one at a time, once the board has loaded (src/roulette.ts). */
  const roulette = useRoulette();
  const rouletteUI = useRouletteUI();
  // The wheel before the task (src/roulette.ts, docs/roulette.md; owner, 2026-10-09: "When pressing
  // a roulette notification, it navigates to the task, revealing the assignment before actually
  // observing the roulette"): a task whose spin this device has not watched is not drawn yet. The
  // screen under it stays (the open board on a load from a push or a link, else the screen the tap
  // came from) while the wheel plays, and Got it draws the task.
  const wheelRoute = useWheelBeforeTask(address, rouletteUI, !!name && !!auth?.authenticated);
  // Suggest something from What's new's "Try it out here" (#/feedback; owner, 2026-10-24: "The what's
  // new entry should have a try it out link"): the form is a sheet, so the screen under it stays the
  // one the person was on (the open board on a fresh load), and sending or cancelling goes back to it.
  const underFeedback = useRef<Route>({ name: "open" });
  if (wheelRoute.name !== "feedback") underFeedback.current = wheelRoute;
  const feedbackLink = wheelRoute.name === "feedback";
  const route = feedbackLink ? underFeedback.current : wheelRoute;
  const routeKey = JSON.stringify(route);
  const slide = useSlide(routeKey, route.name);
  const [showAccount, setShowAccount] = useState(false);
  /** True while the "Add a household" login form is open (started from the account sheet). */
  const [addingHousehold, setAddingHousehold] = useState(false);
  // The content pane takes the focus on every screen, and again once the gates give way to one.
  useFocusOnNavigate(`${routeKey}|${name ? "named" : ""}|${auth?.authenticated ? "in" : ""}|${addingHousehold ? "adding" : ""}`);
  const [showNotify, setShowNotify] = useState(false);
  const [showDev, setShowDev] = useState(false);
  /** Why Run the house was left on its own (a 403: this device is no longer a steward), shown in the account sheet. */
  const [houseNotice, setHouseNotice] = useState<string | null>(null);
  /** The season recap's interstitial and player (src/recap.ts): a board's summary queues the one, a tap opens the other. */
  const recapLayers = useRecapLayers();
  const recapUI = useRecapUI();

  /** Why the auth check failed (offline, or a server error), shown on the gate with a retry. */
  const [authError, setAuthError] = useState<string | null>(null);
  const loadAuth = () => {
    setAuthError(null);
    api.auth.me().then(setAuth, (e: unknown) => setAuthError(e instanceof Error ? e.message : "Could not reach the server"));
  };

  useEffect(() => {
    startTelemetry();
    startPerf();
    startMemoryReports();
    installBackSwipe();
    installDesktopKeys();
    // Start the board's first request in parallel with auth/me instead of after it (T8,
    // docs/performance.md C2): a 401 on the board fails in exactly the same case auth/me reports
    // "not authenticated" (functions/_middleware.ts), so there is nothing to gain by waiting for
    // auth/me before starting it. Only when a name is already stored (nothing to fetch for an
    // anonymous device) and the landing route is actually a board (every other route fetches its
    // own data once auth is known, so a prefetch here would be wasted). The household half of the
    // flags isn't loaded yet this early, so this uses the same local-or-household snapshot the
    // Board's own first render would see (src/features.ts currentFlags).
    const peon = getPeonName();
    const initialRoute = parseHash(location.hash);
    if (peon && (initialRoute.name === "open" || initialRoute.name === "done")) {
      const status = initialRoute.name;
      const flags = currentFlags();
      const opts: BoardRequestOpts = {
        status,
        name: peon,
        since: startOfThisMonth(),
        prev: status === "done" ? startOfLastMonth() : null,
        claims: flags.economyUI,
        flash: status === "open" && (flags.quickUI || flags.lateRushUI),
        notes: status === "open" && flags.notesUI,
      };
      const promise = api.board.summary(opts);
      // Board.tsx's own await is the real consumer and still sees a rejection (a 401 also flips
      // onUnauthorized listeners below, same as a failed auth/me). This branch only exists so an
      // unconsumed prefetch (route changed, the 10s window in src/prefetch.ts lapsed, Board never
      // mounted because auth came back unauthenticated) doesn't surface as an unhandled rejection.
      promise.catch(() => {});
      prefetchBoard(boardRequestKey(opts), promise);
    }
    loadAuth();
    // Coming back online retries by itself, so the gate clears without a tap.
    window.addEventListener("online", loadAuth);
    const stopSound = installPushSoundListener();
    // In the iOS app, APNs listeners go on at startup so a tap that cold-started the app still
    // routes. The module is only fetched inside the app; a browser never loads it.
    let stopNative: (() => void) | null = null;
    let unmounted = false;
    if (isNativeShell()) {
      void installNativePushListeners()
        .then((stop) => (unmounted ? stop() : (stopNative = stop)))
        .catch(() => {});
    }
    // Five taps on the logo open the workshop for stewards; a peon gets a word from the header peon
    // and nothing opens (docs/members.md "The workshop").
    const onDev = () => {
      if (canOpenWorkshop(authRef.current)) setShowDev(true);
      else sayText(WORKSHOP_REFUSAL_LINE);
    };
    window.addEventListener(DEV_EVENT, onDev);
    const stopAuth = onUnauthorized(() => {
      // Only a session that was working: a launch that was never signed in has nothing to explain.
      // The 401 carries no reason, so the line names the household and nothing more.
      const was = authRef.current;
      if (was?.authenticated) setSignedOutNotice(`You were signed out of ${was.household?.name ?? "the household"}.`);
      setAuth((a) => (a ? { ...a, authenticated: false } : a));
    });
    const stopHeld = onNameHeld((held) => {
      rememberHeldNames([held]);
      setPeonName(null);
      setName(null);
      setHeldAs(held);
      setShowAccount(false);
      setShowDev(false);
    });
    return () => {
      stopHeld();
      stopSound();
      unmounted = true;
      stopNative?.();
      stopAuth();
      window.removeEventListener("online", loadAuth);
      window.removeEventListener(DEV_EVENT, onDev);
    };
  }, []);

  // What's new: decided on the first signed-in open of this page load (a brand new device marks
  // every entry seen and shows nothing).
  useEffect(() => {
    if (name && auth?.authenticated) startWhatsNew();
  }, [name, auth?.authenticated]);
  // The weekly scroll: read once per page load with weeklyUI on (a brand new device bookmarks its week
  // and shows nothing, as What's new does).
  useEffect(() => {
    if (name && auth?.authenticated && weeklyUI) startWeekly(() => api.weekly.latest().then((r) => r.scroll));
  }, [name, auth?.authenticated, weeklyUI]);
  // Cheers: read once per page load with cheersUI on, and again when the page comes back after a while.
  useEffect(() => {
    if (name && auth?.authenticated && cheersUI) startCheers(() => api.cheers.news());
  }, [name, auth?.authenticated, cheersUI]);

  // Task roulette: the household's spins, read once per page load while the flag is on (src/roulette.ts).
  useEffect(() => {
    if (name && auth?.authenticated && rouletteUI) startRoulette(() => api.roulette.list());
  }, [name, auth?.authenticated, rouletteUI]);

  // Keep this device's push subscription labelled with its current name, and
  // decide whether the one-time notifications nudge should appear.
  useEffect(() => {
    if (!name || !auth?.authenticated) return;
    void syncPushName(name);
    void shouldShowNotifyPrompt().then(setShowNotify);
  }, [name, auth?.authenticated]);

  // A push notification's URL can carry a ?h=<householdId> marker (server/push.ts) so a tap
  // opens the right household. Runs before the src=push effect below, since both rewrite the
  // query string and this one may trigger a reload.
  useEffect(() => {
    if (!auth?.authenticated) return;
    const params = new URLSearchParams(location.search);
    const h = params.get("h");
    if (!h) return;
    params.delete("h");
    const search = params.toString();
    history.replaceState(null, "", location.pathname + (search ? `?${search}` : "") + location.hash);
    if (!auth.household || h === auth.household.id) return;
    if (!auth.households.some((hh) => hh.id === h)) return;
    void api.auth.switch(h).then(() => {
      resetHouseholdState();
      location.reload();
    });
  }, [auth?.authenticated]);

  // A task notification's URL carries a ?src=push marker (server/push.ts). Once the peon's
  // name is known, record the click and strip the marker so it doesn't linger in the address
  // bar or get carried into a share/bookmark. Stripping it also makes this run only once.
  // (The h= marker above, if present, is already gone by the time this runs.)
  useEffect(() => {
    if (!name || !auth?.authenticated) return;
    if (address.name !== "task" && !(address.name === "open" && address.focus)) return;
    if (!location.search.includes("src=push")) return;
    if (address.name === "task") api.tasks.event(address.id, "notification_clicked", name);
    history.replaceState(null, "", location.pathname + location.hash);
  }, [name, auth?.authenticated, address]);

  /**
   * Signed in as a person with their own password, from the name gate or Change name: the device
   * takes the person's name (its registered spelling). Another household than the one on screen
   * starts the app over there, as a household switch does.
   */
  const adoptPerson = (state: AuthState) => {
    const before = auth?.household?.id ?? null;
    if (state.person?.name) {
      setPeonName(state.person.name);
      setName(state.person.name);
    }
    setHeldAs(null);
    setShowAccount(false);
    forgetPrefetchedBoards();
    hall.forget();
    if (before && state.household && state.household.id !== before) {
      resetHouseholdState();
      location.reload();
      return;
    }
    setAuth(state);
  };

  if (!name) {
    return (
      <NameGate
        key={heldAs ?? ""}
        initialName={heldAs ?? undefined}
        held={heldAs ? [heldAs] : undefined}
        loadHeld={
          auth?.authenticated
            ? () =>
                api.peons.list().then((list) => {
                  const held = heldFrom(list);
                  rememberHeldNames(held);
                  return held;
                })
            : undefined
        }
        onSignedIn={adoptPerson}
        onSubmit={(n) => {
          setPeonName(n);
          setName(n);
          setHeldAs(null);
        }}
      />
    );
  }

  if (!auth) return authError ? <Unreachable message={authError} onRetry={loadAuth} /> : <main class="gate">Loading…</main>;
  if (!auth.authenticated) {
    return (
      <Login
        configured={auth.configured}
        createNeedsCode={auth.create_needs_code}
        joinCode={joinCode}
        onJoinCodeUsed={joinCodeUsed}
        passkeysAvailable={auth.passkeys_available}
        name={name}
        notice={signedOutNotice}
        onSuccess={(state) => {
          clearJoinPath();
          forgetPrefetchedBoards();
          hall.forget();
          setSignedOutNotice(null);
          // A passkey or a person's own password signs the device in as its person: the device takes the person's name.
          if (state.person?.name && state.person.name !== name) {
            setPeonName(state.person.name);
            setName(state.person.name);
          }
          setAuth(state);
        }}
      />
    );
  }
  // A `/join/<code>` link opened on a device already signed in: the same form as "Add another
  // household", on the invite, joining at once. The client cannot tell which household a code is
  // for; a device already in it spends no use and just lands there.
  if (addingHousehold || joinCode) {
    return (
      <Login
        adding
        configured={auth.configured}
        createNeedsCode={auth.create_needs_code}
        joinCode={joinCode}
        onJoinCodeUsed={joinCodeUsed}
        passkeysAvailable={auth.passkeys_available}
        name={name}
        onCancel={() => {
          setAddingHousehold(false);
          joinCodeUsed();
        }}
        onSuccess={(state) => {
          clearJoinPath();
          if (state.person?.name) setPeonName(state.person.name);
          resetHouseholdState();
          location.reload();
        }}
      />
    );
  }

  /** Adopts a switched-to or renamed peon name on this device and closes the account sheet. */
  const applyName = (n: string) => {
    setPeonName(n);
    setName(n);
    setShowAccount(false);
  };
  const openAccount = () => {
    setHouseNotice(null);
    setShowAccount(true);
  };
  /** Refetches who this device is (after a rename, or a 403 that says it is no longer a steward). */
  const refreshAuth = () => api.auth.me().then(setAuth, () => {});

  // The tour, drawn over everything else, the account sheet included (the Profile moment's card sits
  // over the sheet). While one shows, or the first open's welcome is still to come, the other
  // first-run layers wait: the welcome, then (on phones in a browser tab) the install sheet.
  const tourLayer =
    tour.show && !showDev ? (
      <Suspense fallback={null}>
        <Tutorial
          key={tour.show.id}
          steps={tour.show.steps}
          walk={tour.show.moment === null}
          taskId={tour.show.taskId}
          skipCard={tour.show.moment === "first-open"}
          onShown={tourCardsShown}
          onClose={closeTour}
        />
      </Suspense>
    ) : null;
  const holdHints = !!tour.show || tour.firstOpenDue;
  // The launch sheets in order (src/weekly-sheet.ts, src/roulette.ts, shared/weekly.ts): the weekly
  // scroll (owner, 2026-10-05: "it should take priority"), then What's new, then task roulette's
  // wheel (owner, 2026-10-05: "Roulette should come after what's new"). Each waits for the tour and
  // for the ones before it (the scroll due or showing; What's new with entries still to show); the
  // recap and the prompts wait behind all three (a wheel is due while its read is out or a spin is
  // still to show). What's new up first is never cut short by the scroll. A wheel asked for with its
  // task (a push, a link, a card: src/roulette.ts holdRouteForSpin) goes ahead of the scroll and What's
  // new, the tour still winning and the workshop still holding it, but never cuts short one already up.
  if (whatsNew.length === 0) whatsNewUp.current = false;
  if (!weekly.scroll) weeklyUp.current = false;
  const wheelFirst = roulette.held && !weeklyUp.current && !whatsNewUp.current;
  // The workshop's preview ("Show the scroll now") shows whatever the flag says.
  const weeklyShown = !showDev && !wheelFirst && (weeklyUI || weekly.preview) && weeklySheetShows(weekly.scroll, true, holdHints, whatsNewUp.current);
  if (weeklyShown) weeklyUp.current = true;
  const weeklyHold = weeklyUI && whatsNewWaitsForWeekly(weekly.due, whatsNewUp.current);
  const holdWhatsNew = holdHints || weeklyHold;
  const whatsNewShown = !showDev && !wheelFirst && !weeklyShown && whatsNewShows(whatsNew, true, holdWhatsNew);
  if (whatsNewShown) whatsNewUp.current = true;
  const rouletteShown =
    !showDev && !!roulette.spin && (wheelFirst ? rouletteShows(roulette.spin, holdHints) : !weeklyShown && !whatsNewShown && rouletteShows(roulette.spin, holdWhatsNew || whatsNew.length > 0));
  // Cheers (docs/cheers.md): after the wheel, never cutting a launch sheet short: your cheered jobs
  // first (it is about you), then the jobs to cheer, one at a time. The recap and the prompts wait.
  const cheersWaiting = cheersUI && (cheers.cheered.length > 0 || !!cheers.job);
  // The house goal's celebration goes first of these: it is the whole house's.
  const goalShown = !!goalWeek && !showDev && !holdHints && !weeklyShown && !whatsNewShown && !rouletteShown && !weekly.due && whatsNew.length === 0 && !roulette.hold;
  const cheersShown = cheersWaiting && !showDev && !holdHints && !weeklyShown && !whatsNewShown && !rouletteShown && !weekly.due && whatsNew.length === 0 && !roulette.hold;
  const holdPrompts = holdWhatsNew || whatsNew.length > 0 || roulette.hold || cheersWaiting || !!goalWeek;
  const hint = showDev ? (
    <Suspense fallback={null}>
      <DevSheet peonName={name} onClose={() => setShowDev(false)} />
    </Suspense>
  ) : feedbackLink && feedbackUIEnabled ? (
    // Suggest something, opened by What's new's link: over the screen the person was on, the launch
    // sheets waiting behind it. Send or Cancel goes back there; a sent one's line is the peon's.
    <Suspense fallback={null}>
      <FeedbackSheet
        onClosed={(sent) => {
          // Landed straight on the link (a fresh load): the screen under it replaces it, never leaving the app.
          if (previousRouteName() === null) navigate(underFeedback.current, true);
          else back(underFeedback.current);
          if (sent) sayText(sent);
        }}
      />
    </Suspense>
  ) : pendingFind && trinketsUIEnabled ? (
    // Trinkets (docs/trinkets.md "The find"): a chore just turned one up on this device (or the
    // workshop dug one), so the find comes first; the launch sheets wait behind it.
    <Suspense fallback={null}>
      <TrinketFound key={`${pendingFind.key}-${pendingFind.found_at}`} find={pendingFind} onClose={closeFind} />
    </Suspense>
  ) : weeklyShown && weekly.scroll ? (
    // The week's scroll, once, before What's new and the wheel (owner, 2026-10-05: "it should take
    // priority"), after any tour; the others wait for it to close.
    <WeeklySheet scroll={weekly.scroll} onClose={closeWeekly} />
  ) : whatsNewShown ? (
    // After an update, once the board has loaded and no tour is showing (a tour wins; the sheet
    // waits for it to close, in the same open), before the wheel, the recap and the install and notify prompts.
    <Suspense fallback={null}>
      <WhatsNew entries={whatsNew} onClose={closeWhatsNew} />
    </Suspense>
  ) : rouletteShown && roulette.spin ? (
    // Task roulette (docs/roulette.md): each spin this device has not shown, once, newest last, after What's new.
    <Suspense fallback={null}>
      <RouletteWheel key={roulette.spin.id} spin={roulette.spin} me={name} onClose={() => roulette.spin && closeSpin(roulette.spin.id)} />
    </Suspense>
  ) : goalShown && goalWeek ? (
    <Suspense fallback={null}>
      <GoalSheet week={goalWeek} onClose={closeGoal} />
    </Suspense>
  ) : cheersShown && cheers.cheered.length > 0 && name ? (
    <Suspense fallback={null}>
      <CheeredSheet jobs={cheers.cheered} me={name} onClose={closeCheered} />
    </Suspense>
  ) : cheersShown && cheers.job && name ? (
    <Suspense fallback={null}>
      <CheerSheet job={cheers.job} me={name} onNext={nextCheerJob} onLater={closeCheerJobs} />
    </Suspense>
  ) : !holdPrompts && recapLayers.offer && recapUI && !recapLayers.playing ? (
    // After the gate, the name gate and any tour: the household's recap, once (src/recap.ts).
    <RecapIntro recap={recapLayers.offer} />
  ) : !holdPrompts && showInstall ? (
    <InstallHint onClose={() => setShowInstall(false)} />
  ) : !holdPrompts && showNotify ? (
    <NotifyPrompt name={name} onClose={() => setShowNotify(false)} />
  ) : showAccount ? (
    <Suspense fallback={null}>
      <AccountSheet
        name={name}
        household={auth.household}
        households={auth.households}
        person={auth.person ?? null}
        passkeysAvailable={auth.passkeys_available}
        onAuthChanged={(state) => {
          if (state) setAuth(state);
          else void refreshAuth();
        }}
        notice={houseNotice}
        onRunHouse={() => {
          // A screen, not a sheet over a sheet: the account sheet closes, so Back lands on the screen under it.
          setShowAccount(false);
          setHouseNotice(null);
          navigate({ name: "house" });
        }}
        onEditPeon={() => {
          // A screen, as Run the house is: the sheet closes, so Back lands on the screen under it.
          setShowAccount(false);
          navigate({ name: "peon" });
        }}
        onTrinkets={() => {
          // The collection is a screen too (docs/trinkets.md): the sheet closes, Back lands under it.
          setShowAccount(false);
          navigate({ name: "trinkets" });
        }}
        onAddHousehold={() => {
          setShowAccount(false);
          setAddingHousehold(true);
        }}
        onSwitch={applyName}
        onSignedIn={adoptPerson}
        onHelp={() => {
          // The whole tour, from the open board: every card whose feature is on, screen by screen.
          // Its task cards show the first open task, read before the first card.
          setShowAccount(false);
          navigate({ name: "open" });
          api.tasks.list("open").then(
            (tasks) => playWholeTour(tasks),
            () => playWholeTour(null),
          );
        }}
        onClose={() => setShowAccount(false)}
      />
    </Suspense>
  ) : null;

  // The header is the app shell's, not a screen's: one Header at one place in the tree for every
  // route, so a tab press keeps the same peon portrait (sprite, bronze frame, speech bubble) and only
  // the content below changes. Rebuilding it per screen made iOS paint the peon a frame late on
  // every navigation, so he blinked. Tabs carry his speech and your name; every other screen the
  // back arrow to the level above (the same target as the back swipe).
  const header = isTabRoute(route) ? (
    <Header title={screenTitle(route)} speech right={<WhoButton onClick={openAccount} />} />
  ) : (
    <Header title={screenTitle(route)} backTo={parentRoute(route) ?? undefined} />
  );

  // This device's role here, for what it may delete (shared/permissions.ts). Unknown reads as a
  // peon: hiding a control the server would allow beats showing one it refuses.
  const role: Role = auth.household?.role ?? "peon";
  let content: ComponentChildren;
  switch (route.name) {
    // Keyed by tab so each is its own instance: a switch mounts the new board with its remembered
    // state already in place (src/board-cache.ts) instead of re-rendering the old instance once with
    // the previous tab's state before an effect swaps it, which showed for a frame.
    case "open":
      content = <Board key="open" status="open" peonName={name} role={role} focus={route.focus} />;
      break;
    case "done":
      content = <Board key="done" status="done" peonName={name} role={role} />;
      break;
    case "schedules":
      content = <Schedules />;
      break;
    case "task":
      content = <TaskDetail id={route.id} peonName={name} role={role} />;
      break;
    case "newTask":
      content = <TaskForm peonName={name} luxury={route.luxury} />;
      break;
    case "newSchedule":
      content = <ScheduleForm peonName={name} />;
      break;
    case "schedule":
      content = <ScheduleForm id={route.id} peonName={name} />;
      break;
    case "hall":
      content = <HallOfFame />;
      break;
    case "rewards":
      content = <Rewards peonName={name} />;
      break;
    case "usage":
      content = (
        <Suspense
          fallback={
            <main class="content">
              <Spinner />
            </main>
          }
        >
          <Usage />
        </Suspense>
      );
      break;
    case "house":
      content =
        role !== "steward" || !auth.household ? (
          <main class="content">
            <Spinner />
          </main>
        ) : (
          <Suspense
            fallback={
              <main class="content">
                <Spinner />
              </main>
            }
          >
            <House
              household={auth.household}
              passkeysAvailable={auth.passkeys_available}
              onChanged={() => void refreshAuth()}
              onDeleted={() => {
                // As after signing out of that household: another one the device is in, or the gate.
                resetHouseholdState();
                location.reload();
              }}
              onLeave={(message) => {
                back({ name: "open" });
                if (message) {
                  // Refused (403): this device was made a peon meanwhile. The account sheet says so in the server's words.
                  void refreshAuth();
                  setHouseNotice(message);
                  setShowAccount(true);
                }
              }}
            />
          </Suspense>
        );
      break;
    case "peon":
      content = (
        <Suspense
          fallback={
            <main class="content">
              <Spinner />
            </main>
          }
        >
          <PeonEditor peonName={name} />
        </Suspense>
      );
      break;
    case "trinkets":
      content = !trinketsUIEnabled ? (
        <main class="content">
          <Spinner />
        </main>
      ) : (
        <Suspense
          fallback={
            <main class="content">
              <Spinner />
            </main>
          }
        >
          <Trinkets all={route.all === true} />
        </Suspense>
      );
      break;
    case "upgrades":
    case "newUpgrade":
    case "upgrade":
      content = !upgradesUIEnabled ? (
        <main class="content">
          <Spinner />
        </main>
      ) : (
        <Suspense
          fallback={
            <main class="content">
              <Spinner />
            </main>
          }
        >
          {route.name === "upgrades" ? (
            <Upgrades peonName={name} openWood={route.wood} />
          ) : route.name === "newUpgrade" ? (
            <UpgradeForm peonName={name} />
          ) : (
            <UpgradeDetail key={route.id} id={route.id} peonName={name} />
          )}
        </Suspense>
      );
      break;
  }

  // The boards (and every other authenticated route) render from here on, so
  // this is where the household flags start loading and stay live-synced.
  return (
    <>
      <HouseholdFlagsSync />
      <PeonWorkloadSync onOpenBoard={route.name === "open"} />
      <div class="screen" data-slide={slide ?? undefined}>
        {header}
        {offline && (
          <p class="offline-line muted small" role="status">
            Offline
          </p>
        )}
        {content}
      </div>
      {/* Sheets and the tour overlay every screen, so a tour can walk through Rewards or a task. */}
      {hint}
      {tourLayer}
      {/* The recap plays over everything, sheets included, and returns to them when it ends. */}
      {recapLayers.playing && <RecapPlayer recap={recapLayers.playing} />}
    </>
  );
}
